import { LocationStrategy } from '@angular/common';
import { Component, OnInit, Input, Renderer2, AfterViewInit, EventEmitter, Output, OnChanges, SimpleChanges } from '@angular/core';
import { PdfSasService } from '../../_services/pdf-sas.service';
import { finalize } from 'rxjs/operators';
import { configurePdfWorker } from '../pdf-worker';

@Component({
  selector: 'ecpl-pdf-viewer',
  templateUrl: './ecpl-pdf-viewer.component.html',
  styleUrls: ['./ecpl-pdf-viewer.component.css'],
  standalone: false
})
export class EcplPdfViewerComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() id: string;
  @Input() pdfSrc = 'https://vadimdez.github.io/ng2-pdf-viewer/pdf-test.pdf';
  @Input() pdfName: string;
  @Input() isDialog: boolean;
  @Input() forceReopen: number = 0;  // Changes every time file is selected
  @Output() close = new EventEmitter<void>();
  showModalWindow: boolean;
  isProcessingSas: boolean = false;
  sasProcessingComplete: boolean = false;
  sasProcessingError: boolean = false;
  processedPdfSrc: string;

  constructor(
    private renderer: Renderer2,
    location: LocationStrategy,
    private pdfSasService: PdfSasService
  ) {
    configurePdfWorker();
    location.onPopState(() => {
      this.showModalWindow = false;
      this.renderer.removeClass(document.body, 'modal-open');
    });
  }

  showModal() {
    // Process SAS tokens before showing modal
    if (!this.sasProcessingComplete && this.pdfSasService.isPdfUrl(this.pdfSrc)) {
      this.processPdfSasTokens();
    }
    this.showModalWindow = true;
    this.renderer.addClass(document.body, 'modal-open');
  }

  hideModal() {
    this.showModalWindow = false;
    this.renderer.removeClass(document.body, 'modal-open');
    this.close.emit();
  }

  ngOnInit() {
    // Always initialize processedPdfSrc with the input value
    this.processedPdfSrc = this.pdfSrc;
    this.sasProcessingComplete = true;
    
    // Process SAS tokens on initialization if it's a PDF
    if (this.pdfSasService.isPdfUrl(this.pdfSrc)) {
      this.processPdfSasTokens();
    } else {
      // Not a PDF, mark as complete immediately
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // If pdfSrc changes, update processedPdfSrc and re-process SAS tokens if needed
    if (changes['pdfSrc'] && !changes['pdfSrc'].firstChange) {
      this.processedPdfSrc = this.pdfSrc;
      
      if (this.pdfSasService.isPdfUrl(this.pdfSrc)) {
        this.sasProcessingComplete = false;
        this.processPdfSasTokens();
      } else {
        this.sasProcessingComplete = true;
      }
    }
    
    // Show modal when PDF source changes or forceReopen changes
    if ((changes['pdfSrc'] || changes['forceReopen']) && this.isDialog) {
      // Close first if already open
      if (this.showModalWindow) {
        this.hideModal();
      }
      // Then reopen
      setTimeout(() => {
        this.showModal();
      }, 10);
    }
  }

  ngAfterViewInit() {
    if (this.isDialog) {
      this.showModal();
    }
  }

  /**
   * Process PDF SAS tokens before displaying the PDF
   */
  private processPdfSasTokens(): void {
    if (this.isProcessingSas) {
      // Already processing
      return;
    }

    // Set the PDF src immediately so it's always available
    this.processedPdfSrc = this.pdfSrc;
    this.isProcessingSas = true;
    this.sasProcessingError = false;

    this.pdfSasService.processPdfSasTokens(this.pdfSrc)
      .pipe(
        finalize(() => {
          this.isProcessingSas = false;
        })
      )
      .subscribe(success => {
          // Always mark as complete and set the PDF src, regardless of success
          this.sasProcessingComplete = true;
          this.processedPdfSrc = this.pdfSrc;
          
          if (!success) {
            this.sasProcessingError = true;
          }
        },error => {
          console.error('Error processing PDF SAS tokens:', error);
          // Still allow PDF to be displayed even if processing failed
          this.sasProcessingComplete = true;
          this.processedPdfSrc = this.pdfSrc;
          this.sasProcessingError = true;
        }
      );
  }
}
